import { useCallback, useEffect, useRef, useState } from 'react';
import { useRevealEventPasswordMutation } from './useRevealEventPassword.graphql.generated';
import { clearSharedReveal, notifyPasswordChanged, readSharedReveal, retainSharedReveal, runSharedReveal, subscribeToPasswordChanges } from './revealEventPasswordStore';

type RevealedPassword = Readonly<{ eventId: string; value: string | null }>;

// Thrown instead of handing back a plaintext the caller can no longer trust.
export class RevealSupersededError extends Error {
  constructor() {
    super('Event password reveal was superseded');
    this.name = 'RevealSupersededError';
  }
}

export const isRevealSuperseded = (err: unknown): err is RevealSupersededError => err instanceof RevealSupersededError;

export const useRevealEventPassword = (eventId?: string) => {
  const [revealed, setRevealed] = useState<RevealedPassword | null>(null);
  const [revealEventPassword] = useRevealEventPasswordMutation();
  const [isJoiningReveal, setIsJoiningReveal] = useState(false);
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;
  const lastEventIdRef = useRef(eventId);
  const isMountedRef = useRef(true);
  // Bumped by every cancellation; a reveal is only still ours while its generation holds.
  const revealGenerationRef = useRef(0);
  const [instanceId] = useState(() => Symbol());

  // Single cancellation path, so no caller has to know which state a cancellation must touch.
  const cancelAwaitedReveal = useCallback(() => {
    revealGenerationRef.current += 1;
    setIsJoiningReveal(false);
  }, []);

  // Re-mask when another instance changes THAT event's password, not whichever event this
  // instance currently shows — state may still hold a different (now stale) event's plaintext.
  useEffect(() => {
    // Reset on setup, not just on cleanup: a remount reuses the same ref.
    isMountedRef.current = true;
    const unsubscribe = subscribeToPasswordChanges((changedEventId, source) => {
      if (source === instanceId) return;
      if (eventIdRef.current === changedEventId) cancelAwaitedReveal();
      setRevealed(prev => (prev?.eventId === changedEventId ? null : prev));
    });
    return () => {
      unsubscribe();
      eventIdRef.current = undefined;
      isMountedRef.current = false;
    };
  }, [instanceId, cancelAwaitedReveal]);

  useEffect(() => {
    if (!eventId) return undefined;
    return retainSharedReveal(eventId);
  }, [eventId]);

  // Purge a stale event's plaintext during render (not an effect): switching away from an
  // event and back must force a fresh, audited reveal rather than re-showing old state.
  if (revealed && revealed.eventId !== eventId) {
    setRevealed(null);
  }

  // A new event is a new generation, so the previous event's settling reveal cannot clear its spinner.
  if (lastEventIdRef.current !== eventId) {
    lastEventIdRef.current = eventId;
    revealGenerationRef.current += 1;
    if (isJoiningReveal) setIsJoiningReveal(false);
  }

  const password = revealed && eventId && revealed.eventId === eventId ? revealed.value : null;

  const reveal = useCallback(async (): Promise<string | null> => {
    if (password !== null) return password;
    if (!eventId) return null;

    const alreadyShared = readSharedReveal(eventId);
    if (alreadyShared !== null) {
      setRevealed({ eventId, value: alreadyShared });
      return alreadyShared;
    }

    const requestedEventId = eventId;
    const generation = revealGenerationRef.current;
    // Unmounted, event switched, hidden or password changed: one check for all of them.
    const isSuperseded = () => !isMountedRef.current || eventIdRef.current !== requestedEventId || revealGenerationRef.current !== generation;
    setIsJoiningReveal(true);
    try {
      let value: string | null;
      try {
        value = await runSharedReveal(requestedEventId, () =>
          revealEventPassword({ variables: { eventId: requestedEventId } }).then(({ data }) => data?.revealEventPassword ?? null),
        );
      } catch (err) {
        if (isSuperseded()) throw new RevealSupersededError();
        throw err;
      }
      if (isSuperseded()) throw new RevealSupersededError();
      setRevealed({ eventId: requestedEventId, value });
      return value;
    } finally {
      if (isMountedRef.current && revealGenerationRef.current === generation) setIsJoiningReveal(false);
    }
  }, [eventId, password, revealEventPassword]);

  const hide = useCallback(() => {
    cancelAwaitedReveal();
    setRevealed(null);
    if (eventId) clearSharedReveal(eventId);
  }, [eventId, cancelAwaitedReveal]);

  const setKnownPassword = useCallback(
    (value: string) => {
      if (!eventId) return;
      cancelAwaitedReveal();
      setRevealed({ eventId, value });
      notifyPasswordChanged(eventId, instanceId);
    },
    [eventId, instanceId, cancelAwaitedReveal],
  );

  return { password, isRevealed: password !== null, isRevealing: isJoiningReveal, reveal, hide, setKnownPassword };
};
